/**
 * THE COMPANY'S EXPERTS — the catalog's Experts tab (EXPERT-ROOMS.md §18). Every expert another workspace of the
 * company released to Production, searchable (the tab bar's search). A card shows what it is (summary · tags · version
 * · teams / rules); opening one shows everything to READ before subscribing — its teams and their rules, skills,
 * agents, what its work needs from the platform (with sensitivity) and the facts YOUR workspace would keep for it.
 * Subscribe (workspace OWNER / ADMIN) → "Waiting for approval" → "Subscribed"; then any room plugs it in and asks
 * "@<expert>". An expert isn't deployed — it serves you from its own workspace.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { getCompanyExperts, setCompanyExpertSubscription } from '@stepflow/ui-shared/services/companyExpertsApi.js';
import SubscribeDialog, { formHasFields } from '@stepflow/ui-shared/components/SubscriptionForm.jsx';

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
const STATUS = { active: 'Subscribed', pending: 'Waiting for approval', revoked: 'Removed by its owners', own: 'Your workspace’s' };

function ago(iso) {
  const t = Date.parse(iso || '');
  if (!t) return '';
  const d = Math.max(0, Math.round((Date.now() - t) / 86400000));
  return d === 0 ? 'updated today' : `updated ${d} day${d === 1 ? '' : 's'} ago`;
}

export default function ExpertCatalog({ tenantId, query = '', workspaceRole = '', showToast = null, onOpen = null,
  refreshKey = 0 }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const isAdmin = ['OWNER', 'ADMIN'].includes(String(workspaceRole || '').toUpperCase());
  const q = query.trim().length >= 2 ? query.trim() : '';
  useEffect(() => {
    if (!tenantId) return undefined;
    let cancelled = false;
    const t = setTimeout(() => {
      getCompanyExperts(tenantId, { q }).then((d) => { if (!cancelled) setRows(d.experts || []); });
    }, q ? 250 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [tenantId, q, refreshKey]);
  // a card opens the expert's FULL detail page (the same page style as a registry network's) — the host owns it
  const openOne = (x) => { onOpen?.(x); };
  const act = async (x, action) => {
    setBusy(true);
    try {
      const d = await setCompanyExpertSubscription(tenantId, action, x);
      setRows(d.experts || []);
    } catch (e) {
      showToast?.(`Couldn't ${action} (${e.message}).`, 'error');
    } finally {
      setBusy(false);
    }
  };
  // §14b — an expert with a subscription form opens the popup; an open one subscribes in one click
  const [asking, setAsking] = useState(null);
  const subscribe = (x) => (formHasFields(x.subscriptionForm) ? setAsking(x) : act(x, 'subscribe'));
  const action = (x) => {
    if (!isAdmin || x.own) return null;
    if (!x.status || x.status === 'revoked') {
      if (x.retireAt) return null;                     // §20c — retiring: it takes no new workspaces
      return <button type="button" disabled={busy} data-testid={`xcat-subscribe-${x.expertNetworkId}`}
        className={`${btn} bg-blue-600 text-white hover:bg-blue-700`} onClick={() => subscribe(x)}>Subscribe</button>;
    }
    return <button type="button" disabled={busy} className={`${btn} text-gray-600 hover:bg-gray-100`}
      onClick={() => act(x, 'unsubscribe')}>{x.status === 'pending' ? 'Withdraw request' : 'Unsubscribe'}</button>;
  };
  const cards = useMemo(() => rows || [], [rows]);
  if (rows === null) return <p className="p-6 text-xs text-gray-400">Loading…</p>;
  return (
    <div className="flex min-h-0 flex-1 gap-4 overflow-hidden p-5" data-testid="expert-catalog">
      <SubscribeDialog open={!!asking} expertName={asking?.name} form={asking?.subscriptionForm}
        onCancel={() => setAsking(null)}
        onSubmit={async (profile) => {
          const d = await setCompanyExpertSubscription(tenantId, 'subscribe', asking, profile);
          setRows(d.experts || []);
          setAsking(null);
          showToast?.(`Subscribed to ${asking.name} with “${profile.name}”.`, 'success');
        }} />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!cards.length ? (
          <p className="text-xs text-gray-500" data-testid="xcat-empty">
            {q ? `No expert matches “${q}”.` : 'No expert is shared with you yet. A workspace releases one to Production and shares it.'}
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {cards.map((x) => (
              <li key={`${x.expertTenantId}/${x.expertNetworkId}`} data-testid={`xcat-card-${x.expertNetworkId}`}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <button type="button" className="w-full text-left" onClick={() => openOne(x)}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">{x.name}</span>
                    <span className="rounded bg-violet-50 px-1.5 py-px text-[10px] text-violet-700">Expert</span>
                    {x.label ? <span className="text-[10px] text-gray-500">{x.label}</span> : null}
                    {x.retireAt ? <span className="rounded bg-amber-50 px-1.5 py-px text-[10px] text-amber-700"
                      data-testid={`xcat-retiring-${x.expertNetworkId}`} title={x.retireNote || ''}>
                      Retiring {String(x.retireAt).slice(0, 10)}</span> : null}
                  </div>
                  {x.summary ? <p className="mt-1 line-clamp-2 text-xs text-gray-600">{x.summary}</p> : null}
                  <p className="mt-1 text-[11px] text-gray-500">
                    {x.tenantName ? `${x.tenantName} · ` : ''}{x.teamCount ? `${x.teamCount} teams · ${x.ruleCount} rules · ` : ''}{ago(x.at)}
                  </p>
                  {(x.tags || []).length ? (
                    <div className="mt-1 flex flex-wrap gap-1">{x.tags.map((t) => (
                      <span key={t} className="rounded bg-gray-100 px-1.5 py-px text-[10px] text-gray-600">{t}</span>))}</div>
                  ) : null}
                </button>
                <div className="mt-2 flex items-center gap-2 text-[11px]">
                  {x.status ? <span className="text-gray-600" data-testid={`xcat-status-${x.expertNetworkId}`}>{STATUS[x.status] || x.status}</span> : null}
                  <span className="ml-auto">{action(x)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
